import { LoadingOutlined } from '@ant-design/icons';
import { Widget } from '@typeform/embed-react';
import { Spin } from 'antd';
import { useSession } from 'next-auth/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import styled, { css } from 'styled-components';

import theme from '../../../assets/theme';
import useAccessBadge from '../../../hooks/useAccessBadge';
import { OrganizationType } from '../../../hooks/useAccessBadge';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import { useMetrics } from '../../../hooks/useMetrics';
import { useMarketingMetrics } from '../../../providers/PlausibleAnalyticsProvider';
import { ById, SchoolRoleType } from '../../../services/backendService/types';
import { Input } from '../../_atoms/_Input';
import { Button } from '../../_atoms/Button';
import { Center } from '../../_atoms/Center';
import OrganizationSelector, {
  SelectedLocationType,
  SelectedOrganizationType,
} from '../../_molecules/OrganizationSelector';

type FormStep = 'role' | 'organization' | 'form';

export interface HybridFormProps {
  /**
   * The TypeForm URL, e.g. 'https://form.typeform.com/to/nO1rBQ87'
   */
  typeformUrl: string;
  thankYouMessage: string;
  metricEventNamePrefix: string;
}

const HybridForm: React.FC<HybridFormProps> = ({
  typeformUrl,
  thankYouMessage,
  metricEventNamePrefix,
}) => {
  const session = useSession();
  const { accessBadge, isFetched } = useAccessBadge();
  const metrics = useMetrics(true);
  const { track } = useMarketingMetrics();
  const isDesktop = useBreakPoint(900);
  const { showNewMessages } = useIntercom();
  const isLoading = !isFetched && session.status !== 'unauthenticated';

  // Extract TypeForm ID from URL
  const typeformId = useMemo(() => {
    const match = typeformUrl.match(/\/to\/([^#?]+)/);
    return match ? match[1] : '';
  }, [typeformUrl]);

  // Form step state
  const [currentStep, setCurrentStep] = useState<FormStep | null>(null);
  const [isDone, setIsDone] = useState(false);

  // Role selection state
  const [role, setRole] = useState<SchoolRoleType>();
  const [customRole, setCustomRole] = useState<string>();

  // Organization selection state
  const [
    selectedOrganization,
    setSelectedOrganization,
  ] = useState<SelectedOrganizationType | null>(null);
  const [organizationType, setOrganizationType] = useState<OrganizationType | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<SelectedLocationType | null>(
    null,
  );
  const [hideOrganizationTypePicker, setHideOrganizationTypePicker] = useState<boolean>(
    false,
  );
  const [customOrganizationName, setCustomOrganizationName] = useState<string>();
  const [needsCustomOrganizationName, setNeedsCustomOrganizationName] = useState<boolean>(
    false,
  );
  const [locationFetchError, setLocationFetchError] = useState<string | undefined>();

  // Check if organization selection is complete
  const shouldShowNextButtonAfterOrgSelection = useCallback(() => {
    if (selectedOrganization) {
      return true;
    }

    if (needsCustomOrganizationName && customOrganizationName) {
      return true;
    }

    const isInternationalOrIncomplete =
      selectedLocation?.place?.country && selectedLocation?.place?.country !== 'US';

    return isInternationalOrIncomplete && customOrganizationName ? true : false;
  }, [
    selectedOrganization,
    needsCustomOrganizationName,
    customOrganizationName,
    selectedLocation,
  ]);

  // Validate step can be accessed based on current state
  const validateAndSetStep = useCallback(
    (requestedStep: FormStep) => {
      // Role step is always accessible
      if (requestedStep === 'role') {
        setCurrentStep('role');
        return;
      }

      // Organization step requires role selection
      if (requestedStep === 'organization') {
        if (role) {
          setCurrentStep('organization');
        } else {
          // Redirect to role step and update URL
          setCurrentStep('role');
          const url = new URL(window.location.href);
          url.searchParams.set('step', 'role');
          window.history.replaceState({}, '', url);
        }
        return;
      }

      // Form step requires organization selection
      if (requestedStep === 'form') {
        if (!role) {
          // Redirect to role step and update URL
          setCurrentStep('role');
          const url = new URL(window.location.href);
          url.searchParams.set('step', 'role');
          window.history.replaceState({}, '', url);
        } else if (!shouldShowNextButtonAfterOrgSelection()) {
          // Redirect to organization step and update URL
          setCurrentStep('organization');
          const url = new URL(window.location.href);
          url.searchParams.set('step', 'organization');
          window.history.replaceState({}, '', url);
        } else {
          setCurrentStep('form');
        }
      }
    },
    [role, shouldShowNextButtonAfterOrgSelection],
  );

  // Setup browser history integration for form steps
  useEffect(() => {
    // Skip organization and role steps if user has activeTenantId
    if (accessBadge) {
      setCurrentStep('form');
      const url = new URL(window.location.href);
      url.searchParams.set('step', 'form');
      window.history.replaceState({}, '', url);
      return;
    }

    // Check if there's a step in the URL when the component mounts
    const params = new URLSearchParams(window.location.search);
    const stepFromURL = params.get('step');

    if (stepFromURL && ['role', 'organization', 'form'].includes(stepFromURL)) {
      // Validate that required state for step is present
      validateAndSetStep(stepFromURL as FormStep);
    } else {
      // Initialize URL with role step without affecting history
      const url = new URL(window.location.href);
      url.searchParams.set('step', 'role');
      window.history.replaceState({}, '', url);
      setCurrentStep('role');
    }

    // Set up popstate listener for browser back/forward buttons
    const handlePopState = () => {
      // If user has activeTenantId, always stay on form step
      if (accessBadge) {
        setCurrentStep('form');
        return;
      }

      // When user hits back/forward, get step from URL
      const params = new URLSearchParams(window.location.search);
      const stepFromURL = params.get('step');

      if (stepFromURL && ['role', 'organization', 'form'].includes(stepFromURL)) {
        // Validate step can be accessed based on current state
        validateAndSetStep(stepFromURL as FormStep);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [accessBadge, validateAndSetStep]);

  // Prepare hidden fields for TypeForm
  const hiddenFields = useMemo(() => {
    const fields: ById<string> = {};
    // Access badge fields
    if (accessBadge?.user?.email) {
      fields['email'] = accessBadge.user.email;
    }
    if (accessBadge?.user?.id) {
      fields['user_id'] = accessBadge.user.id;
    }
    if (accessBadge?.user?.profileInfo?.role) {
      fields['role'] = accessBadge.user.profileInfo.role;
    }
    if (accessBadge?.organization?.id) {
      fields['organization_id'] = accessBadge.organization.id;
    }
    if (accessBadge?.organization?.name) {
      fields['organization_name'] = accessBadge.organization.name;
    }

    if (accessBadge?.organization?.type) {
      fields['organization_type'] = accessBadge.organization.type;
    }

    if (accessBadge?.organization?.region) {
      fields['organization_region'] = accessBadge.organization.region;
    }

    if (accessBadge?.organization?.country) {
      fields['organization_country'] = accessBadge.organization.country;
    }

    if (accessBadge?.organization?.city) {
      fields['organization_city'] = accessBadge.organization.city;
    }

    // Role fields
    if (role) {
      fields['role'] = role;
    }
    if (role === 'Other' && customRole) {
      fields['custom_role'] = customRole;
    }

    // Organization fields
    const org = selectedOrganization?.organization;
    if (org) {
      if (org.name) {
        fields['organization_name'] = org.name;
      }
      if (org.id) {
        fields['organization_id'] = org.id;
      }
      if (org.city) {
        fields['organization_city'] = org.city;
      }
      if (org.type) {
        fields['organization_type'] = org.type;
      }
      if (org.region) {
        fields['organization_region'] = org.region;
      }
      if (org.country) {
        fields['organization_country'] = org.country;
      }
    }
    return fields;
  }, [accessBadge, role, customRole, selectedOrganization]);

  // Memoized TypeForm widget
  const typeformWidget = useMemo(
    () => (
      <Widget
        hidden={hiddenFields}
        id={typeformId}
        medium="curipod-admin-embed"
        height={490}
        width={isDesktop ? 600 : 350}
        onSubmit={() => setIsDone(true)}
        onEndingButtonClick={() => setIsDone(true)}
        autoFocus={true}
        inlineOnMobile
      />
    ),
    [hiddenFields, isDesktop, typeformId],
  );

  // Get TypeForm URL with hidden fields
  const getTypeFormUrlWithParams = useCallback(() => {
    const params = new URLSearchParams();

    Object.entries(hiddenFields).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    return `${typeformUrl}#${params.toString()}`;
  }, [hiddenFields, typeformUrl]);

  // Organization handlers
  const handleChangeOrgType = useCallback(
    (newOrgType: OrganizationType | null) => {
      if (organizationType && organizationType !== newOrgType) {
        setSelectedOrganization(null);
        setCustomOrganizationName(undefined);
        setNeedsCustomOrganizationName(false);
      }
      setOrganizationType(newOrgType);
    },
    [organizationType],
  );

  const handleChangeLocation = useCallback(
    (location: SelectedLocationType | null) => {
      if (
        selectedLocation?.placeAutocomplete?.place_id !==
        location?.placeAutocomplete?.place_id
      ) {
        setSelectedOrganization(null);
        setCustomOrganizationName(undefined);
        setNeedsCustomOrganizationName(false);
      }
      setSelectedLocation(location);
    },
    [selectedLocation],
  );

  const handleChangeOrganization = useCallback(
    (organization: SelectedOrganizationType | null) => {
      setSelectedOrganization(organization);
    },
    [],
  );

  // Role selection handlers
  const handleRoleSelect = useCallback(
    (selectedRole: SchoolRoleType) => {
      track(`${metricEventNamePrefix}.Role.Option.Selected`, {
        props: {
          selectedOption: selectedRole,
        },
      });
      metrics.logEvent(`${metricEventNamePrefix}.Role.Option.Selected`, {
        selectedOption: selectedRole,
      });
      setRole(selectedRole);
    },
    [track, metrics, metricEventNamePrefix],
  );

  const handleRoleNext = useCallback(() => {
    track(`${metricEventNamePrefix}.Next.Button.Clicked`, {
      props: {
        selectedRole: role,
      },
    });
    metrics.logEvent(`${metricEventNamePrefix}.Next.Button.Clicked`, {
      selectedRole: role,
    });

    // Add history entry for next step
    const url = new URL(window.location.href);
    url.searchParams.set('step', 'organization');
    window.history.pushState({}, '', url);

    setCurrentStep('organization');

    // Set organization type based on role
    switch (role) {
      case 'Teacher':
      case 'School Administrator':
        handleChangeOrgType(OrganizationType.School);
        setHideOrganizationTypePicker(true);
        break;
      case 'District Administrator':
        handleChangeOrgType(OrganizationType.District);
        setHideOrganizationTypePicker(true);
        break;
      default:
        handleChangeOrgType(null);
        setHideOrganizationTypePicker(false);
        break;
    }
  }, [role, track, metrics, handleChangeOrgType, metricEventNamePrefix]);

  const handleOrganizationNext = useCallback(() => {
    track(`${metricEventNamePrefix}.Next.Button.Clicked`, {
      props: {
        selectedOrganization: selectedOrganization?.organization?.name,
      },
    });
    metrics.logEvent(`${metricEventNamePrefix}.Next.Button.Clicked`, {
      selectedOrganization: selectedOrganization?.organization?.name,
    });

    // Add history entry for next step
    const url = new URL(window.location.href);
    url.searchParams.set('step', 'form');
    window.history.pushState({}, '', url);

    setCurrentStep('form');
  }, [selectedOrganization, track, metrics, metricEventNamePrefix]);

  // Role selection component
  const renderRoleSelection = useCallback(() => {
    const roleOptions = [
      'Teacher',
      'Instructional/Tech Coach',
      'Library Media Specialist',
      'School Administrator',
      'District Administrator',
      'Purchasing Manager',
      'Other',
    ];

    const shouldDescribeCustomOption = role === 'Other';

    return (
      <>
        <h1>What is your primary role?</h1>
        <OptionsContainer>
          {roleOptions.map((option, i) => (
            <StyledOption
              $isSelected={role === option}
              key={i}
              onClick={() => handleRoleSelect(option as SchoolRoleType)}
            >
              {option}
            </StyledOption>
          ))}
        </OptionsContainer>
        {shouldDescribeCustomOption && (
          <div>
            <p style={{ textAlign: 'left', width: '100%', marginBottom: 0 }}>
              Other role
            </p>
            <Input
              maxLength={50}
              style={{ width: 250, fontSize: 16 }}
              placeholder="Enter your role"
              onChange={e => setCustomRole(e.target.value)}
              value={customRole}
            />
          </div>
        )}
        <Button disabled={role === undefined} onClick={handleRoleNext}>
          Next
        </Button>
      </>
    );
  }, [role, customRole, handleRoleSelect, handleRoleNext]);

  // Organization selection component
  const renderOrganizationSelection = useCallback(() => {
    return (
      <>
        {hideOrganizationTypePicker && organizationType ? (
          <h1>Select your {organizationType}</h1>
        ) : (
          <h1>Select your School/District</h1>
        )}
        <OrganizationSelector
          organizationType={organizationType}
          onSelectOrganizationType={handleChangeOrgType}
          onSelectLocation={handleChangeLocation}
          selectedLocation={selectedLocation}
          onSelectOrganization={handleChangeOrganization}
          selectedOrganization={selectedOrganization}
          hideOrganizationTypePicker={hideOrganizationTypePicker}
          customOrganizationName={customOrganizationName}
          onSetCustomOrganizationName={setCustomOrganizationName}
          locationFetchError={locationFetchError}
          onLocationFetchError={setLocationFetchError}
          needsCustomOrganizationName={needsCustomOrganizationName}
          onSetNeedsCustomOrganizationName={setNeedsCustomOrganizationName}
          disableSearchRetry={false}
          fluid
          metricEventNamePrefix={metricEventNamePrefix}
        />

        {shouldShowNextButtonAfterOrgSelection() && (
          <Button onClick={handleOrganizationNext} styles={{ marginTop: 20 }}>
            Next
          </Button>
        )}
      </>
    );
  }, [
    hideOrganizationTypePicker,
    organizationType,
    handleChangeOrgType,
    handleChangeLocation,
    selectedLocation,
    handleChangeOrganization,
    selectedOrganization,
    customOrganizationName,
    locationFetchError,
    needsCustomOrganizationName,
    shouldShowNextButtonAfterOrgSelection,
    handleOrganizationNext,
    metricEventNamePrefix,
  ]);

  // Form content
  const renderFormContent = useCallback(() => {
    return (
      <>
        <h1 style={{ width: '100%', textAlign: 'center', marginBottom: 0 }}>
          {isDone ? 'Thank you!' : 'Tell us a bit more about yourself'}
        </h1>
        <EmbeddedContainer>
          {!isDone && typeformWidget}

          {isDone && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <p
                style={{
                  width: '100%',
                  maxWidth: isDesktop ? '500px' : '300px',
                  textAlign: 'center',
                  fontSize: 18,
                  lineHeight: '1.5',
                }}
              >
                {thankYouMessage}
              </p>
            </div>
          )}

          {!isDone && (
            <p style={{ width: '100%', textAlign: 'center' }}>
              If the form is not showing after a few seconds,{' '}
              <A
                style={{ textDecoration: 'underline !important' }}
                href={getTypeFormUrlWithParams()}
                target="_blank"
                rel="noreferrer"
              >
                Click here
              </A>
            </p>
          )}
        </EmbeddedContainer>
      </>
    );
  }, [isDone, isDesktop, typeformWidget, getTypeFormUrlWithParams, thankYouMessage]);
  // If loading, show spinner for entire component
  if (isLoading || currentStep === null) {
    return (
      <OuterWrapper>
        <FormContainer>
          <Center style={{ width: '100%', height: 300, padding: '10px' }}>
            <Spin
              indicator={
                <LoadingOutlined
                  style={{ fontSize: 24, color: theme.colors.orange }}
                  spin
                />
              }
            />
          </Center>
          <p style={{ width: '100%', textAlign: 'center', fontWeight: 500 }}>
            Need help with the form?{' '}
            <ChatLink onClick={() => showNewMessages()}>Chat</ChatLink> with us.
          </p>
        </FormContainer>
      </OuterWrapper>
    );
  }

  return (
    <OuterWrapper>
      <FormContainer>
        {currentStep === 'role' && renderRoleSelection()}
        {currentStep === 'organization' && renderOrganizationSelection()}
        {currentStep === 'form' && renderFormContent()}
        <p style={{ width: '100%', textAlign: 'center', fontWeight: 500 }}>
          Need help with the form?{' '}
          <ChatLink onClick={() => showNewMessages()}>Chat</ChatLink> with us.
        </p>
      </FormContainer>
    </OuterWrapper>
  );
};

const OuterWrapper = styled.div`
  width: 100%;
  padding: 0 10px;
  display: flex;
  justify-content: center;
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 800px;
  background-color: ${theme.colors.white};
  display: flex;
  flex-direction: column;
  gap: 30px;
  align-items: center;
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.1);
  border-radius: 15px;
  padding: 20px 10px;
`;

const A = styled.a`
  &&& {
    text-decoration: underline !important;
  }
`;

const EmbeddedContainer = styled.div`
  display: grid;
  justify-content: center;
  align-content: center;
  width: 100%;
  overflow: hidden;

  & > div {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const OptionsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 15px;
  align-items: center;
  justify-content: center;
`;

const StyledOption = styled.div<{ $isSelected: boolean }>`
  padding: 10px 20px;
  border-radius: 30px;
  background-color: ${theme.colors.white};
  color: ${theme.colors.black};
  font-weight: 600;
  font-size: 16px;
  text-align: center;
  border: 1px solid ${theme.colors.black};
  cursor: pointer;
  transition: 0.2s ease-in-out;

  &:hover {
    background-color: ${theme.colors.grey};
    color: ${({ $isSelected }) =>
      $isSelected ? theme.colors.green : theme.colors.black};
    border: 1px solid ${theme.colors.white};
  }

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      color: ${theme.colors.green};
      border: 1px solid ${theme.colors.green};
    `}
`;

const ChatLink = styled.a`
  color: ${theme.colors.green};
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: ${theme.colors.orange};
  }
`;

export default HybridForm;
